import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { assistanceAPI, filesPlatformAPI, peoplePlatformAPI } from '@/services';
import { mapPeopleListResponse } from '../models/memberModel';
import { useToast } from './Toast';
import ModalShell from './ModalShell';
import MmSelect from './MmSelect';
import { searchFieldsMatch } from '../utils/searchText';
import {
  ASSISTANCE_EVENT_CATEGORY_KEYS,
  coveredCategoriesForMechanism,
  eligibilityFormFromMechanism,
  formatEligibilitySummary,
  normalizeAssistanceCategoryCode,
  suggestedAmountForCategory,
} from '../utils/assistanceEventCategories';

const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx';
const MAX_FILE_BYTES = 10 * 1024 * 1024;

function memberLabel(m) {
  const last = String(m?.last_name || '').trim();
  const first = String(m?.first_name || '').trim();
  if (last && first) return `${last}, ${first}`;
  return String(m?.name || '').trim() || String(m?.id || '');
}

async function uploadSupportingFiles(files, t) {
  const fileIds = [];
  for (const file of files || []) {
    const uploaded = await filesPlatformAPI.upload(file);
    const fid =
      uploaded?.data?.id ||
      uploaded?.data?.file_id ||
      uploaded?.data?.upload_id ||
      '';
    if (!fid) {
      throw new Error(t('assistanceManagement.fileIdMissing'));
    }
    fileIds.push(String(fid));
  }
  return fileIds;
}

function toFriendlyAssistanceCreateError(err, t) {
  const detail = String(err?.response?.data?.detail || '').trim();
  const lower = detail.toLowerCase();
  const p = 'assistanceManagement.newCaseModal.errors';
  if (!detail) return t(`${p}.createImpossible`);

  if (lower.includes('event_category is not covered')) {
    return t(`${p}.eventNotCovered`);
  }
  if (lower.includes('requested_amount is below mechanism min_amount')) {
    return t(`${p}.belowMechMin`);
  }
  if (lower.includes('requested_amount exceeds mechanism max_amount')) {
    return t(`${p}.exceedsMechMax`);
  }
  if (lower.includes('requested_amount is below category min_amount')) {
    return t(`${p}.belowCatMin`);
  }
  if (lower.includes('requested_amount exceeds category max_amount')) {
    return t(`${p}.exceedsCatMax`);
  }
  if (lower.includes('requested_amount is below configured min_amount')) {
    return t(`${p}.belowCfgMin`);
  }
  if (lower.includes('requested_amount exceeds configured max_amount')) {
    return t(`${p}.exceedsCfgMax`);
  }
  if (lower.includes('member does not meet minimum seniority')) {
    return t(`${p}.seniorityMin`);
  }
  if (lower.includes('member seniority is insufficient')) {
    return t(`${p}.seniorityInsufficient`);
  }
  if (lower.includes('member contribution situation is not compliant')) {
    return t(`${p}.contributionNotCompliant`);
  }
  if (lower.includes('member contribution situation is not up to date')) {
    const m = String(detail || '').match(/(\d+)\s+unpaid month/i);
    if (m) {
      return t(`${p}.contributionNotUpToDateCount`, { count: m[1] });
    }
    return t(`${p}.contributionNotUpToDate`);
  }
  if (lower.includes('member status is not eligible')) {
    return t(`${p}.memberInactive`);
  }
  if (lower.includes('member profile not found for eligibility')) {
    return t(`${p}.memberNotFound`);
  }
  if (lower.includes('reason is required by assistance workflow')) {
    return t(`${p}.reasonRequired`);
  }
  if (lower.includes('mechanism is not active')) {
    return t(`${p}.mechanismInactive`);
  }
  if (lower.includes('solidarity mechanism not found')) {
    return t(`${p}.mechanismNotFound`);
  }
  if (lower.includes('project context required')) {
    return t(`${p}.projectContextRequired`);
  }
  return detail;
}

/**
 * Création d’une demande d’allocation : mécanisme, membre, catégorie filtrée, montant suggéré.
 */
const AssistanceNewCaseModal = ({
  open,
  onClose,
  mechanisms,
  fixedRequestedAmount,
  canCreate,
  preselectedMemberId,
  lockMemberSelection = false,
  onCreated,
  /** Mode édition (membre) : brouillon ou en instruction, pas encore approuvé */
  editingCase = null,
}) => {
  const { t } = useTranslation('common');
  const { success, error } = useToast();
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [members, setMembers] = useState([]);
  const [memberQuery, setMemberQuery] = useState('');
  const [mechanismId, setMechanismId] = useState('');
  const [memberProfileId, setMemberProfileId] = useState('');
  const [eventCategory, setEventCategory] = useState('');
  const [reason, setReason] = useState('');
  const [amountText, setAmountText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notifyMember, setNotifyMember] = useState(true);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [existingFileIds, setExistingFileIds] = useState([]);
  const fileInputRef = useRef(null);

  const nk = 'assistanceManagement.newCaseModal';

  const selectedMechanism = useMemo(
    () => (Array.isArray(mechanisms) ? mechanisms : []).find((m) => String(m?.id) === String(mechanismId)),
    [mechanisms, mechanismId]
  );

  const selectedEligibility = useMemo(
    () => (selectedMechanism ? eligibilityFormFromMechanism(selectedMechanism) : null),
    [selectedMechanism]
  );

  const categoryOptions = useMemo(() => {
    const covered = coveredCategoriesForMechanism(selectedMechanism);
    const keys = covered.length ? covered : ASSISTANCE_EVENT_CATEGORY_KEYS;
    return keys.map((key) => ({
      value: key,
      label: t(`memberDashboard.eventCategory.${key}`, { defaultValue: key }),
    }));
  }, [selectedMechanism, t]);

  useEffect(() => {
    if (!open || !canCreate) return;
    if (lockMemberSelection) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingMembers(true);
        const res = await peoplePlatformAPI.list({ limit: 2000, skip: 0 });
        if (!cancelled) setMembers(mapPeopleListResponse(res.data));
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setMembers([]);
          error(e?.response?.data?.detail || t(`${nk}.loadMembersError`));
        }
      } finally {
        if (!cancelled) setLoadingMembers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, canCreate, lockMemberSelection, error, t]);

  useEffect(() => {
    if (!open) return;
    if (editingCase) {
      setMemberQuery('');
      setMechanismId(String(editingCase.mechanism_id || '').trim());
      setMemberProfileId(String(editingCase.member_profile_id || preselectedMemberId || '').trim());
      setEventCategory(normalizeAssistanceCategoryCode(editingCase.event_category));
      setReason(String(editingCase.reason || ''));
      setPendingFiles([]);
      setExistingFileIds(
        Array.isArray(editingCase.supporting_file_ids)
          ? editingCase.supporting_file_ids.map((id) => String(id || '').trim()).filter(Boolean)
          : []
      );
      setAmountText(
        editingCase.requested_amount != null && editingCase.requested_amount !== ''
          ? String(editingCase.requested_amount)
          : ''
      );
    } else {
      setMemberQuery('');
      setMechanismId('');
      setMemberProfileId(String(preselectedMemberId || '').trim());
      setEventCategory('');
      setReason('');
      setNotifyMember(true);
      setPendingFiles([]);
      setExistingFileIds([]);
      setAmountText(
        fixedRequestedAmount != null && Number(fixedRequestedAmount) > 0
          ? String(fixedRequestedAmount)
          : ''
      );
    }
  }, [open, preselectedMemberId, editingCase, fixedRequestedAmount]);

  useEffect(() => {
    if (!open || editingCase) return;
    if (!selectedMechanism) return;
    const covered = coveredCategoriesForMechanism(selectedMechanism);
    if (covered.length && eventCategory && !covered.includes(eventCategory)) {
      setEventCategory(covered[0] || '');
      return;
    }
    if (!eventCategory && covered.length === 1) {
      setEventCategory(covered[0]);
    }
  }, [open, editingCase, selectedMechanism, eventCategory]);

  useEffect(() => {
    if (!open || editingCase) return;
    if (!selectedMechanism || !eventCategory) return;
    const suggested = suggestedAmountForCategory(
      selectedMechanism,
      eventCategory,
      fixedRequestedAmount
    );
    if (suggested > 0) {
      setAmountText(String(suggested));
    }
  }, [open, editingCase, selectedMechanism, eventCategory, fixedRequestedAmount]);

  const filteredMembers = useMemo(() => {
    if (!String(memberQuery || '').trim()) return members.slice(0, 80);
    return members
      .filter((m) => searchFieldsMatch(memberQuery, [memberLabel(m), m.id, m.email, m.phone]))
      .slice(0, 120);
  }, [members, memberQuery]);

  const selectedMember = useMemo(
    () => members.find((m) => String(m.id) === String(memberProfileId)),
    [members, memberProfileId]
  );

  const amountNum = Number(String(amountText || '').replace(/\s/g, '').replace(',', '.'));
  const amountOk = Number.isFinite(amountNum) && amountNum > 0;
  const isDraftEdit = !editingCase || String(editingCase.status || '').toLowerCase() === 'draft';

  const handleMechanismChange = (nextId) => {
    setMechanismId(nextId);
    if (!editingCase) {
      setEventCategory('');
    }
  };

  const handlePickFiles = (fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    const accepted = [];
    for (const file of picked) {
      if (Number(file.size) > MAX_FILE_BYTES) {
        error(t(`${nk}.fileTooLarge`, { name: file.name || 'fichier' }));
        continue;
      }
      accepted.push(file);
    }
    if (!accepted.length) return;
    setPendingFiles((prev) => [...prev, ...accepted]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate) return;
    if (
      !String(mechanismId).trim() ||
      !String(memberProfileId).trim() ||
      !String(eventCategory).trim() ||
      !String(reason).trim()
    ) {
      error(t(`${nk}.validateFill`));
      return;
    }
    if (!amountOk) {
      error(editingCase ? t(`${nk}.validateAmountEdit`) : t(`${nk}.validateAmountSettings`));
      return;
    }
    try {
      setSubmitting(true);
      const uploadedIds = pendingFiles.length
        ? await uploadSupportingFiles(pendingFiles, t)
        : [];
      if (editingCase?.id) {
        const patch = {
          event_category: normalizeAssistanceCategoryCode(eventCategory),
          reason: String(reason).trim(),
          requested_amount: amountNum,
        };
        if (isDraftEdit) {
          patch.mechanism_id = String(mechanismId).trim();
        }
        await assistanceAPI.updateCase(String(editingCase.id), patch);
        if (uploadedIds.length) {
          await assistanceAPI.attachFiles(String(editingCase.id), uploadedIds);
        }
        success(t(`${nk}.successUpdated`));
      } else {
        const res = await assistanceAPI.createCase({
          mechanism_id: String(mechanismId).trim(),
          member_profile_id: String(memberProfileId).trim(),
          event_category: normalizeAssistanceCategoryCode(eventCategory),
          reason: String(reason).trim(),
          requested_amount: amountNum,
          supporting_file_ids: uploadedIds,
          ...(!lockMemberSelection ? { notify_member: notifyMember } : {}),
        });
        const createdId = String(res?.data?.id || '').trim();
        if (createdId && uploadedIds.length && !Array.isArray(res?.data?.supporting_file_ids)) {
          await assistanceAPI.attachFiles(createdId, uploadedIds);
        }
        const st = String(res?.data?.status || '').toLowerCase();
        success(
          st === 'submitted' ? t(`${nk}.successCreatedSubmitted`) : t(`${nk}.successCreated`)
        );
      }
      onCreated?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      error(toFriendlyAssistanceCreateError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <ModalShell
      maxWidthClass="max-w-lg"
      onBackdropClick={() => !submitting && onClose?.()}
      panelClassName="flex min-h-0 flex-col !overflow-hidden p-0"
    >
      <div className="mm-modal-header">
        <div className="min-w-0">
          <p className="mm-hero__label text-[#1f6ab8]">{t('memberPortal.allocation')}</p>
          <h2 className="mt-0.5 font-display text-lg font-extrabold tracking-tight text-[#0f3350]">
            {editingCase ? t(`${nk}.titleEdit`) : t(`${nk}.titleCreate`)}
          </h2>
          <p className="mt-1 text-xs leading-snug text-slate-600">
            {editingCase ? (
              t(`${nk}.subtitleEdit`)
            ) : amountOk ? (
              t(`${nk}.subtitleReferenceOk`)
            ) : (
              <>
                {t(`${nk}.subtitleAmountMissingBeforeLink`)}{' '}
                <Link
                  to="/settings?tab=assistance"
                  className="font-semibold text-[#113a5b] underline-offset-2 hover:underline"
                >
                  {t(`${nk}.settingsLink`)}
                </Link>
                {t(`${nk}.subtitleAmountMissingAfter`)}
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => !submitting && onClose?.()}
          className="mm-press flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
          aria-label={t(`${nk}.closeAria`)}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-3 sm:space-y-4 sm:px-5 sm:py-4">
          <div className="mm-card space-y-3 p-3 sm:p-3.5">
            <div>
              <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                {t(`${nk}.labelMechanism`)}
              </label>
              <MmSelect
                value={mechanismId}
                onChange={handleMechanismChange}
                disabled={submitting || (Boolean(editingCase) && !isDraftEdit)}
                required
                placeholder={t(`${nk}.mechanismPlaceholder`)}
                options={mechanisms.map((m) => ({
                  value: m.id,
                  label: m.name || m.id,
                }))}
              />
              {selectedMechanism ? (
                <div className="mt-2 rounded-xl border border-slate-200/90 bg-slate-50/80 px-3 py-2 text-xs text-slate-600">
                  <p className="font-semibold text-slate-700">
                    {t('assistanceManagement.eligibilityLabel')}{' '}
                    {formatEligibilitySummary(selectedMechanism, t)}
                  </p>
                  {selectedEligibility?.text ? (
                    <p className="mt-1 whitespace-pre-wrap leading-snug text-slate-500">
                      {selectedEligibility.text}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>

            {lockMemberSelection ? (
              <div>
                <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                  {t(`${nk}.labelBeneficiary`)}
                </label>
                <div className="rounded-xl border border-[#bcd4f6] bg-gradient-to-r from-[#edf4ff] to-white px-3 py-2.5 text-sm font-medium text-[#0f3350]">
                  {t(`${nk}.lockMemberHint`)}
                </div>
              </div>
            ) : (
              <div>
                <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                  {t(`${nk}.labelBeneficiary`)}
                </label>
                <input
                  type="search"
                  value={memberQuery}
                  onChange={(e) => setMemberQuery(e.target.value)}
                  placeholder={t(`${nk}.searchMembersPlaceholder`)}
                  disabled={submitting || loadingMembers}
                  className="mm-field mb-2"
                />
                {selectedMember ? (
                  <div className="mb-2 flex flex-wrap items-center gap-2 rounded-xl border border-[#bcd4f6] bg-gradient-to-r from-[#edf4ff] to-white px-3 py-2.5 text-sm">
                    <span className="font-bold text-[#0f3350]">{memberLabel(selectedMember)}</span>
                    <span className="text-xs text-slate-600">
                      {selectedMember.email || selectedMember.phone || selectedMember.id}
                    </span>
                    <button
                      type="button"
                      className="ml-auto text-xs font-bold text-[#113a5b]"
                      onClick={() => setMemberProfileId('')}
                    >
                      {t(`${nk}.changeMember`)}
                    </button>
                  </div>
                ) : null}
                <div className="max-h-44 overflow-y-auto rounded-xl border border-slate-200/90 bg-white/70">
                  {loadingMembers ? (
                    <p className="p-4 text-center text-sm text-slate-500">{t(`${nk}.loadingMembers`)}</p>
                  ) : filteredMembers.length === 0 ? (
                    <p className="p-4 text-center text-sm text-slate-500">{t(`${nk}.emptyMemberSearch`)}</p>
                  ) : (
                    <ul className="divide-y divide-slate-100/90">
                      {filteredMembers.map((m) => (
                        <li key={m.id}>
                          <button
                            type="button"
                            onClick={() => setMemberProfileId(String(m.id))}
                            className={`mm-press flex w-full flex-col items-start px-3 py-2.5 text-left text-sm ${
                              String(memberProfileId) === String(m.id)
                                ? 'bg-[#edf4ff] ring-1 ring-inset ring-[#2f6fd4]/35'
                                : 'hover:bg-slate-50'
                            }`}
                          >
                            <span className="font-bold text-[#0f3350]">{memberLabel(m)}</span>
                            <span className="text-xs text-slate-600">
                              {[m.email, m.phone].filter(Boolean).join(' · ') || m.id}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mm-card space-y-3 p-3 sm:p-3.5">
            <div>
              <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                {t(`${nk}.labelEventCategory`)}
              </label>
              <MmSelect
                value={eventCategory}
                onChange={setEventCategory}
                disabled={submitting || !mechanismId}
                required
                placeholder={t(`${nk}.eventCategoryPlaceholder`)}
                options={categoryOptions}
              />
              <p className="mt-1.5 text-xs text-slate-500">
                {selectedMechanism
                  ? t(`${nk}.eventCategoriesFromMechanism`)
                  : t(`${nk}.eventCategoriesNote`)}
              </p>
            </div>

            <div>
              <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                {t(`${nk}.labelReason`)}
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                required
                disabled={submitting}
                placeholder={t(`${nk}.reasonPlaceholder`)}
                className="mm-field"
              />
            </div>

            <div>
              <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                {t(`${nk}.labelRequestedAmount`)}
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={amountText}
                onChange={(e) => setAmountText(e.target.value)}
                disabled={submitting}
                required
                className="mm-field"
              />
              <p className="mt-1.5 text-xs text-slate-500">{t(`${nk}.amountThresholdHint`)}</p>
            </div>

            <div>
              <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                {t(`${nk}.labelAttachments`)}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_FILE_TYPES}
                disabled={submitting}
                onChange={(e) => handlePickFiles(e.target.files)}
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-[#edf4ff] file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-[#113a5b]"
              />
              <p className="mt-1.5 text-xs text-slate-500">{t(`${nk}.attachmentsHint`)}</p>
              {existingFileIds.length > 0 ? (
                <ul className="mt-2 space-y-1 rounded-xl border border-slate-200/90 bg-white/70 px-3 py-2 text-xs text-slate-600">
                  {existingFileIds.map((fid) => (
                    <li key={fid} className="truncate">
                      {t(`${nk}.existingAttachment`, { id: fid })}
                    </li>
                  ))}
                </ul>
              ) : null}
              {pendingFiles.length > 0 ? (
                <ul className="mt-2 space-y-1.5">
                  {pendingFiles.map((file, index) => (
                    <li
                      key={`${file.name}-${file.size}-${index}`}
                      className="flex items-center gap-2 rounded-xl border border-[#bcd4f6] bg-[#edf4ff]/60 px-3 py-2 text-sm text-[#0f3350]"
                    >
                      <span className="min-w-0 flex-1 truncate font-medium">{file.name}</span>
                      <span className="shrink-0 text-xs text-slate-500">
                        {(Number(file.size) / 1024).toFixed(0)} Ko
                      </span>
                      <button
                        type="button"
                        disabled={submitting}
                        onClick={() => removePendingFile(index)}
                        className="shrink-0 text-xs font-bold text-red-700 hover:underline"
                      >
                        {t(`${nk}.removeAttachment`)}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            {!lockMemberSelection && !editingCase ? (
              <label className="flex items-start gap-2.5 rounded-xl border border-slate-200/90 bg-slate-50/70 px-3 py-2.5 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#2f6fd4] focus:ring-[#2f6fd4]"
                  checked={notifyMember}
                  disabled={submitting}
                  onChange={(e) => setNotifyMember(e.target.checked)}
                />
                <span>
                  <span className="font-medium">{t(`${nk}.notifyMemberLabel`)}</span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    {t(`${nk}.notifyMemberHelp`)}
                  </span>
                </span>
              </label>
            ) : null}
          </div>
        </div>

        <div className="mm-modal-footer shrink-0">
          <button
            type="button"
            onClick={() => !submitting && onClose?.()}
            className="mm-cta mm-cta--ghost flex-1"
          >
            {t('assistanceManagement.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || !canCreate || !amountOk}
            className="mm-cta flex-1 disabled:opacity-50"
          >
            {submitting
              ? editingCase
                ? t(`${nk}.savingEdit`)
                : t(`${nk}.savingCreate`)
              : editingCase
                ? t(`${nk}.submitEdit`)
                : t(`${nk}.submitCreate`)}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

export default AssistanceNewCaseModal;
